/**
 * 练习中心
 * ------------------------------------------------------------
 * 四个模块（单词 / 句子 / 语法 / 综合）共用这一个页面：
 *   - 顶部选模块（route 参数 module）
 *   - 单词：7 级选择卡
 *   - 句子：8 级选择卡（显示"在哪一级卡住"）
 *   - 语法：先选分类（分类来自导入内容）+ 题型 + 题量
 *   - 综合：题量/来源/策略
 *
 * 生成题目统一走 generators，页面只负责收集参数并跳到 session 页。
 */
import { state, saveSelection } from '../core/store.js';
import { listUnits, listTextbooks, getUnitContent } from '../services/dataSource.js';
import { SENTENCE_LEVELS } from '../engines/generators/sentenceLevels.js';
import { grammarCategoriesOf } from '../engines/generators/grammarGenerator.js';
import { groupGrammarByPoint } from '../engines/grammarPoints.js';
import { COMPREHENSIVE_PRESETS } from '../engines/generators/comprehensiveGenerator.js';
import { masteryOf, errorsOf } from '../services/records.js';
import { card, emptyState, tag } from '../components/ui.js';
import { navigate } from '../core/router.js';
import { escapeHtml, toast } from '../utils/index.js';
import { startFlow } from './flow.js';
import { unlockAudio } from '../components/feedback.js';
import { primeAudio, selfTest } from '../services/audioService.js';
import { progressBar } from '../components/ui.js';

const MODULES = [
  { key: 'word', name: '单词学习', icon: '🔤', desc: '一条完整流程：认识 → 认义 → 拼写 → 默写 → 用一用' },
  { key: 'sentence', name: '句子学习', icon: '📝', desc: '一条完整流程：读懂 → 3 秒记忆 → 补全 → 重排 → 译写' },
  { key: 'grammar', name: '语法专项', icon: '🧩', desc: '按分类练习，题型可选' },
  { key: 'comprehensive', name: '综合练习', icon: '📋', desc: '模拟单元卷，可选题量与来源' },
];

export default async function renderPractice(mount, params = {}) {
  const moduleKey = MODULES.some(m => m.key === params.module) ? params.module : 'word';
  const books = await listTextbooks();

  if (!books.length || !state.selection.textbookId) {
    mount.innerHTML = `<div class="page-head"><h1>✏️ 练习中心</h1></div>
      ${emptyState('📚', '还没有可用教材', '先添加教材才能开始练习', '<a class="btn btn-primary" href="#/textbooks">去教材管理</a>')}`;
    return;
  }

  const units = await listUnits(state.selection.textbookId);
  const unit = units.find(u => u.id === state.selection.unitId) || units[0];
  if (!unit) {
    mount.innerHTML = `<div class="page-head"><h1>✏️ 练习中心</h1></div>
      ${emptyState('🗂️', '这本教材还没有单元', '先导入单元内容', '<a class="btn btn-primary" href="#/import">去导入</a>')}`;
    return;
  }

  const content = await getUnitContent(state.selection.textbookId, unit.id);
  const mastery = await masteryOf(unit.id);
  const errs = await errorsOf(unit.id);

  mount.innerHTML = `
    <div class="page-head">
      <h1>✏️ 练习中心</h1>
      <p class="lead">当前单元：Unit ${unit.no} ${escapeHtml(unit.title || '')} ·
        ${content.words.length} 词 / ${content.sentences.length} 句 / ${content.grammar.length} 语法点</p>
    </div>

    <div class="module-tabs">
      ${MODULES.map(m => `
        <button class="module-tab ${m.key === moduleKey ? 'on' : ''}" data-module="${m.key}" type="button">
          <span class="mt-icon">${m.icon}</span>
          <span class="mt-body"><b>${m.name}</b><small>${m.desc}</small></span>
        </button>`).join('')}
    </div>

    <div id="module-body"></div>`;

  mount.querySelectorAll('[data-module]').forEach(btn => {
    btn.addEventListener('click', () => navigate('#/practice', { module: btn.dataset.module }));
  });

  const body = mount.querySelector('#module-body');
  const selection = { textbookId: state.selection.textbookId, unitId: unit.id };
  if (moduleKey === 'word') renderWordLevels(body, content, mastery, errs, selection);
  else if (moduleKey === 'sentence') renderSentenceLevels(body, content, mastery, errs, selection);
  else if (moduleKey === 'grammar') renderGrammar(body, content, errs);
  else renderComprehensive(body, content, errs);
}

/* ------------------------------ 单词：一体化学习入口 ------------------------------ */

/** 单词学习不拆关卡：一条连续流程，内部自动穿插 6 种训练方式 */
function renderWordLevels(body, content, mastery, errs, selection) {
  if (!content.words.length) {
    body.innerHTML = card(emptyState('🔤', '本单元没有单词', '导入教材内容后即可学习',
      '<a class="btn btn-primary" href="#/import">去导入</a>'));
    return;
  }

  const words = content.words;
  const mastered = words.filter(w => {
    const m = mastery.find(x => x.refType === 'word' && x.refId === w.id);
    const levels = m?.levels || {};
    return ['1', '2', '3', '4', '5', '6'].every(k => levels[k] === 'pass');
  }).length;
  const pct = Math.round((mastered / words.length) * 100);

  body.innerHTML = card(`
    <div class="flow-intro">
      <div class="fi-icon">🔤</div>
      <div class="fi-body">
        <h3>一次学完 Unit 的全部单词</h3>
        <p class="muted">系统会带你走一条完整流程：<b>听音认识 → 看英文选中文 → 看中文选英文 → 听音拼写 → 独立默写 → 例句填空</b>，
        最后做一小段「用一用」。中间穿插出现，不会连着背同一个词。<br>
        每个单词<b>独立答对 3 次</b>才算真正掌握，答错的会自动再排回来。</p>
      </div>
    </div>
    <div class="unit-progress">
      <div class="row between" style="font-size:13px;margin-bottom:6px">
        <span>本单元 ${words.length} 个单词</span>
        <b>已掌握 ${mastered}</b>
      </div>
      ${progressBar(pct)}
    </div>
    <div class="row mt-16" style="gap:10px;flex-wrap:wrap">
      <button class="btn btn-primary btn-lg" id="start-all" type="button">🔊 开始学习（本单元全部 ${words.length} 个）</button>
      <button class="btn btn-ghost" id="start-10" type="button">先学 10 个</button>
      ${errs.filter(e => e.refType === 'word').length ? `<button class="btn btn-ghost" id="start-err" type="button">🔁 先练错过的词</button>` : ''}
      <button class="btn btn-ghost" id="test-audio" type="button">🔊 测试发音</button>
    </div>
    <div class="audio-note" id="test-audio-note" hidden></div>

    <details class="pick-box mt-16" id="word-pick">
      <summary>☑ 自己挑今天要学的单词</summary>
      <div class="pick-actions">
        <button class="btn btn-sm btn-ghost" data-pick="all" type="button">全选</button>
        <button class="btn btn-sm btn-ghost" data-pick="none" type="button">取消全选</button>
        <span class="muted small" id="pick-count"></span>
      </div>
      <div class="pick-list">
        ${words.map(w => `
          <label class="pick-item">
            <input type="checkbox" data-id="${escapeHtml(w.id)}" value="${escapeHtml(w.en)}">
            <span class="pi-en">${escapeHtml(w.en)}</span>
            <span class="pi-zh">${escapeHtml(w.zh || '')}</span>
          </label>`).join('')}
      </div>
      <button class="btn btn-primary btn-block mt-8" id="start-picked" type="button" disabled>▶️ 学习勾选的单词</button>
    </details>

    <div class="word-preview mt-16">
      ${words.slice(0, 24).map(w => `<span class="wchip">${escapeHtml(w.en)}</span>`).join('')}
    </div>`, { title: '单词学习' });

  // 关键：必须在这次用户手势里同步发起音频解锁，否则后续自动播放会被浏览器静音
  const go = extra => {
    unlockAudio();                       // 音效（WebAudio）
    primeAudio();                        // HTML5 Audio（内置 MP3）+ TTS 热身
    startFlow({ ...selection, module: 'word', ...extra });
  };
  body.querySelector('#start-all')?.addEventListener('click', () => go({}));
  body.querySelector('#start-10')?.addEventListener('click', () => go({ limit: 10 }));
  body.querySelector('#start-err')?.addEventListener('click', () => go({ limit: 10 }));

  // 「🔊 测试发音」：直接在平板上验证这条链路（解锁 → MP3 → 播放 → 报告结果）
  body.querySelector('#test-audio')?.addEventListener('click', async () => {
    const note = body.querySelector('#test-audio-note');
    note.hidden = false;
    note.textContent = '正在测试…';
    const sample = words[0];
    const rep = await selfTest({ en: sample.en, audio: sample.audio });
    note.innerHTML = `${rep.ok ? '✅' : '⚠️'} 发音测试（示例词：${escapeHtml(sample.en)}）<br>`
      + rep.steps.map(x => `· ${escapeHtml(x)}`).join('<br>');
  });

  // 自定义勾选
  const pick = body.querySelector('#word-pick');
  const countEl = pick?.querySelector('#pick-count');
  const startBtn = pick?.querySelector('#start-picked');
  const updateCount = () => {
    const n = pick.querySelectorAll('input:checked').length;
    if (countEl) countEl.textContent = n ? `已选 ${n} / ${words.length} 个` : '';
    if (startBtn) {
      startBtn.disabled = n === 0;
      startBtn.textContent = n ? `▶️ 学习勾选的 ${n} 个单词` : '▶️ 学习勾选的单词';
    }
  };
  pick?.querySelectorAll('input').forEach(cb => cb.addEventListener('change', updateCount));
  pick?.querySelectorAll('[data-pick]').forEach(btn => btn.addEventListener('click', () => {
    const on = btn.dataset.pick === 'all';
    pick.querySelectorAll('input').forEach(cb => { cb.checked = on; });
    updateCount();
  }));
  startBtn?.addEventListener('click', () => {
    const ids = Array.from(pick.querySelectorAll('input:checked')).map(cb => cb.dataset.id);
    if (!ids.length) return toast('先勾选要学的单词', 'warn');
    go({ ids, title: `单词学习（已选 ${ids.length} 个）` });
  });
}

/* ------------------------------ 句子：一体化学习入口 ------------------------------ */

function renderSentenceLevels(body, content, mastery, errs, selection) {
  if (!content.sentences.length) {
    body.innerHTML = card(emptyState('📝', '本单元没有句子', '导入教材内容后即可学习',
      '<a class="btn btn-primary" href="#/import">去导入</a>'));
    return;
  }

  const sents = content.sentences;
  const stuck = SENTENCE_LEVELS.map(lv => {
    const rows = mastery.filter(m => m.refType === 'sentence' && m.levels?.[String(lv.level)]);
    return { lv, fail: rows.filter(r => r.levels[String(lv.level)] === 'fail').length };
  });

  body.innerHTML = card(`
    <div class="flow-intro">
      <div class="fi-icon">📝</div>
      <div class="fi-body">
        <h3>一次学完 Unit 的重点句子</h3>
        <p class="muted">完整流程：<b>读懂句子 → 3 秒闪卡记忆（然后藏起来）→ 补全句子 → 单词重排 → 中文译英文 → 句型替换 → 用一用</b>。<br>
        句型替换会照样子改，例如 <code>There is a door.</code> → <code>There are two doors.</code></p>
      </div>
    </div>
    <div class="row mt-16" style="gap:10px;flex-wrap:wrap">
      <button class="btn btn-primary btn-lg" id="start-all" type="button">▶️ 学完本单元全部句子（${sents.length} 句）</button>
      <button class="btn btn-ghost" id="start-5" type="button">先学 5 句</button>
      ${errs.filter(e => e.refType === 'sentence').length ? `<button class="btn btn-ghost" id="start-err" type="button">🔁 先练错过的句子</button>` : ''}
    </div>

    <details class="pick-box mt-16" id="sent-pick">
      <summary>☑ 自己挑今天要学的句子</summary>
      <div class="pick-actions">
        <button class="btn btn-sm btn-ghost" data-pick="all" type="button">全选</button>
        <button class="btn btn-sm btn-ghost" data-pick="none" type="button">取消全选</button>
        <span class="muted small" id="pick-count"></span>
      </div>
      <div class="pick-list">
        ${sents.map(s => `
          <label class="pick-item">
            <input type="checkbox" data-id="${escapeHtml(s.id)}" value="${escapeHtml(s.en)}">
            <span class="pi-en">${escapeHtml(s.en)}</span>
            <span class="pi-zh">${escapeHtml(s.zh || '')}</span>
          </label>`).join('')}
      </div>
      <button class="btn btn-primary btn-block mt-8" id="start-picked" type="button" disabled>▶️ 学习勾选的句子</button>
    </details>

    <div class="insight-bar mt-16">${renderSentenceInsight(stuck)}</div>`, { title: '句子学习' });

  const go = extra => {
    unlockAudio();
    primeAudio();
    startFlow({ ...selection, module: 'sentence', ...extra });
  };
  body.querySelector('#start-all')?.addEventListener('click', () => go({}));
  body.querySelector('#start-5')?.addEventListener('click', () => go({ limit: 5 }));
  body.querySelector('#start-err')?.addEventListener('click', () => go({ limit: 5 }));

  // 自定义勾选
  const pick = body.querySelector('#sent-pick');
  const countEl = pick?.querySelector('#pick-count');
  const startBtn = pick?.querySelector('#start-picked');
  const updateCount = () => {
    const n = pick.querySelectorAll('input:checked').length;
    if (countEl) countEl.textContent = n ? `已选 ${n} / ${sents.length} 句` : '';
    if (startBtn) {
      startBtn.disabled = n === 0;
      startBtn.textContent = n ? `▶️ 学习勾选的 ${n} 句` : '▶️ 学习勾选的句子';
    }
  };
  pick?.querySelectorAll('input').forEach(cb => cb.addEventListener('change', updateCount));
  pick?.querySelectorAll('[data-pick]').forEach(btn => btn.addEventListener('click', () => {
    const on = btn.dataset.pick === 'all';
    pick.querySelectorAll('input').forEach(cb => { cb.checked = on; });
    updateCount();
  }));
  startBtn?.addEventListener('click', () => {
    const ids = Array.from(pick.querySelectorAll('input:checked')).map(cb => cb.dataset.id);
    if (!ids.length) return toast('先勾选要学的句子', 'warn');
    go({ ids, title: `句子学习（已选 ${ids.length} 句）` });
  });
}

function renderSentenceInsight(stuck) {
  const withFail = stuck.filter(s => s.fail > 0).sort((a, b) => b.fail - a.fail)[0];
  if (!withFail) return `<span class="muted">这里会告诉你最容易卡在哪个环节。</span>`;
  return `<span class="sym-hint">⚠</span> 之前最容易卡在 <b>${escapeHtml(withFail.lv.name)}</b>（${withFail.fail} 次），这一轮会多练几次。`;
}

/* ------------------------------ 语法专项 ------------------------------ */

function renderGrammar(body, content, errs) {
  const groups = groupGrammarByPoint(content);
  const gqs = content.questions.filter(q => q.skill === 'G');

  if (!groups.length && !gqs.length) {
    body.innerHTML = card(emptyState('🧩', '本单元没有语法内容', '导入含语法点的教材内容后即可练习',
      '<a class="btn btn-primary" href="#/import">去导入</a>'));
    return;
  }

  body.innerHTML = card(`
    <p class="muted" style="font-size:12.5px;margin-bottom:14px">
      语法点来自教材/导入内容，不由程序臆造。点哪个知识点，就<b>只出哪个知识点的题</b>；点「混合练习」才随机混在一起。
      每个知识点下面标注的是该知识点在教材里已有的原题数量。
    </p>
    <div class="gcat-grid">
      <button class="gcat-card on" data-cat="" type="button">
        <span class="gc-icon">🎲</span><b>混合练习</b>
        <small>${gqs.length} 题 · 随机多个语法点</small>
      </button>
      ${groups.map(g => {
        const n = g.questions.length;
        const label = n ? `${n} 道教材原题` : `${g.points.length} 个语法点`;
        // data-cat 用知识点 id；raw: 开头的回退到原名，生成器按名字匹配
        const catKey = g.id.startsWith('raw:') ? g.name : g.id;
        return `<button class="gcat-card" data-cat="${escapeHtml(catKey)}"
                  data-label="${escapeHtml(g.name)}" title="${escapeHtml(g.desc || '')}" type="button">
          <span class="gc-icon">${g.icon}</span><b>${escapeHtml(g.name)}</b>
          <small>${label}</small>
        </button>`;
      }).join('')}
    </div>
    <div class="form-grid mt-8">
      <label>题型
        <select class="input" id="g-type">
          <option value="mixed">混合（推荐）</option>
          <option value="bank-only">只做教材原题</option>
          <option value="grammar-only">只做规则判断</option>
        </select>
      </label>
      <label>题量
        <select class="input" id="g-count">
          <option value="10">10 题</option>
          <option value="20">20 题</option>
          <option value="30">30 题</option>
        </select>
      </label>
    </div>
    <div class="row mt-8" style="gap:10px">
      <button class="btn btn-primary" id="g-start" type="button">▶️ 开始练习</button>
      ${errs.filter(e => e.refType === 'grammar').length
        ? `<button class="btn btn-ghost" id="g-errors" type="button">🔁 错题优先</button>` : ''}
    </div>`, { title: '语法专项' });

  let pickedCat = '';
  const startBtn = body.querySelector('#g-start');
  const syncStartLabel = () => {
    const label = pickedCat
      ? (body.querySelector('.gcat-card.on')?.dataset.label || '所选知识点')
      : '混合';
    if (startBtn) startBtn.textContent = `▶️ 开始练习（${label}）`;
  };
  body.querySelectorAll('.gcat-card').forEach(btn => btn.addEventListener('click', () => {
    body.querySelectorAll('.gcat-card').forEach(b => b.classList.remove('on'));
    btn.classList.add('on');
    pickedCat = btn.dataset.cat;
    syncStartLabel();
  }));
  syncStartLabel();

  const startGrammar = extra => startSession({
    generatorId: 'grammar',
    count: Number(body.querySelector('#g-count').value),
    module: 'grammar',
    category: pickedCat,
    mode: body.querySelector('#g-type').value,
    ...extra,
  });
  body.querySelector('#g-start')?.addEventListener('click', () => startGrammar({}));
  body.querySelector('#g-errors')?.addEventListener('click', () => startGrammar({ count: 10, strategy: 'errors' }));
}

/* ------------------------------ 综合练习 ------------------------------ */

function renderComprehensive(body, content, errs) {
  body.innerHTML = card(`
    <p class="muted" style="font-size:12.5px;margin-bottom:14px">
      模拟单元卷：按单词 40% / 句子 35% / 语法 25% 配比抽题。可以选「错题优先」或「薄弱优先」。
    </p>
    <div class="preset-grid">
      ${COMPREHENSIVE_PRESETS.map(p => `
        <button class="preset-card" data-count="${p.count}" type="button">
          <div class="preset-num">${p.count}</div>
          <div class="preset-name">${escapeHtml(p.name)}</div>
          <div class="preset-desc">${escapeHtml(p.desc)}</div>
        </button>`).join('')}
    </div>
    <div class="form-grid mt-8">
      <label>抽题策略
        <select class="input" id="c-strategy">
          <option value="random">随机</option>
          <option value="errors">错题优先${errs.length ? `（${errs.length}）` : ''}</option>
          <option value="weak">薄弱优先</option>
          <option value="mixed">混合（错题+薄弱）</option>
        </select>
      </label>
      <label>题目来源
        <select class="input" id="c-mode">
          <option value="mixed">全部（教材原题 + 生成题）</option>
          <option value="textbook-only">只用教材原题</option>
          <option value="generated-only">只用自动生成题</option>
        </select>
      </label>
    </div>`, { title: '综合练习' });

  body.querySelectorAll('[data-count]').forEach(btn => {
    btn.addEventListener('click', () => {
      startSession({
        generatorId: 'comprehensive',
        count: Number(btn.dataset.count),
        module: 'comprehensive',
        strategy: body.querySelector('#c-strategy').value,
        mode: body.querySelector('#c-mode').value,
      });
    });
  });
}

/** 统一跳转到答题页；参数通过 sessionStorage 传（避免 URL 太长） */
function startSession(cfg) {
  if (!state.selection.unitId) {
    toast('请先选择单元', 'warn');
    return;
  }
  sessionStorage.setItem('pending-session', JSON.stringify({
    ...cfg,
    textbookId: state.selection.textbookId,
    unitId: state.selection.unitId,
    at: Date.now(),
  }));
  navigate('#/session');
}
